import $ from 'jquery';

import ProductModel from 'chairisher/model/product';

import { trackFavoritingEvent } from 'chairisher/analytics/product';
import { trackRemarketingEvent } from 'chairisher/util/analytics';
import { abbreviateNumber } from 'chairisher/util/format';
import { isPlainObject } from 'chairisher/util/type';
import { getQuickCreateUrl, isAuthenticated } from 'chairisher/context/auth';
import {
    addFavoritedProductId,
    canRenderFavoriteHearts,
    getDataForVariantId,
    getFavoriteCount,
    getFavoriteCreateUrl,
    getFavoriteDeleteUrl,
    getFavoriteLookupUrl,
    getFavoritedProductIds,
    getProductObjectById,
    removeFavoritedProductId,
    setFavoritedProductIds,
    setFolderedProductIds,
    UNIT_TYPE_SAMPLE,
} from 'chairisher/context/product';

/**
 * Manages view layer functionality for favoriting products
 */
class FavoriteProductView {
    constructor() {
        this.anonymousFavoriteThreshold = 1;
        this.dataUpdateDeferred = null;
        this.favoriteCount = getFavoriteCount();
        this.favoriteCountSelectors = [];
        this.favoriteButtonSelector = '';
        this.productFavoriteCountSelector = '';
        this.favoriteTrackingLabel = '';

        setFavoritedProductIds(getFavoritedProductIds());
    }

    /**
     * @param {Array.<string>} favoriteCountSelectors The list of element selectors listening for updates
     */
    setFavoriteCountSelectors(favoriteCountSelectors) {
        if (Array.isArray(favoriteCountSelectors)) {
            this.favoriteCountSelectors = favoriteCountSelectors;
        }
    }

    /**
     * @returns {Array.<string>} The list of element selectors listening for updates
     */
    getFavoriteCountSelectors() {
        return this.favoriteCountSelectors;
    }

    /**
     * @param {string} productFavoriteCountSelector The element selector stub listening for updates
     */
    setProductFavoriteCountSelector(productFavoriteCountSelector) {
        this.productFavoriteCountSelector = productFavoriteCountSelector;
    }

    /**
     * @returns {string} The element selector stub listening for updates
     */
    getProductFavoriteCountSelector() {
        return this.productFavoriteCountSelector;
    }

    /**
     * @param {string} favoriteButtonSelector The selector for the favorite button
     */
    setFavoriteButtonSelector(favoriteButtonSelector) {
        this.favoriteButtonSelector = favoriteButtonSelector;
    }

    /**
     * @returns {string} The selector for the favorite button
     */
    getFavoriteButtonSelector() {
        return this.favoriteButtonSelector;
    }

    /**
     * @param {string} label The tracking label for the favorite button
     */
    setFavoriteTrackingLabel(label) {
        this.favoriteTrackingLabel = label;
    }

    /**
     * @returns {string} The tracking label for the favorite button
     */
    getFavoriteTrackingLabel() {
        return this.favoriteTrackingLabel;
    }

    /**
     * Updates the current user's total favorite count
     *
     * @param {number=} count
     */
    updateFavoriteCount(count) {
        const selectors = this.getFavoriteCountSelectors();
        if (selectors?.length) {
            const selector = selectors.join(',');
            if (count != null) {
                this.favoriteCount = count;
            }

            $(selector).text(abbreviateNumber(this.favoriteCount) || '');
        }
    }

    /**
     * Checks the server to see which product ids that are currently present on the page are favorites (if any).
     *
     * @returns {$.Deferred}
     */
    updateFavoriteProductIds() {
        if (this.dataUpdateDeferred !== null && this.dataUpdateDeferred.state() === 'pending') {
            return this.dataUpdateDeferred;
        }

        this.dataUpdateDeferred = $.Deferred();

        if (isAuthenticated()) {
            const $products = $('[data-product-id]');
            const idsOnPage = new Set();
            $products.each((i) => {
                const id = $products.eq(i).data('product-id');
                if (id && Number.isFinite(id)) {
                    idsOnPage.add(id);
                }
            });
            if (idsOnPage.size > 0) {
                $.ajax({
                    url: getFavoriteLookupUrl(),
                    data: {
                        product_ids: Array.from(idsOnPage).join(','),
                    },
                }).done((data) => {
                    const folderedProductIds = []; // TODO: (CHAIR-9033) move this into a more appropriate place

                    if (data.action_info) {
                        data.action_info.forEach((info) => {
                            if (info.is_in_folder) {
                                folderedProductIds.push(info.id);
                            }
                        });
                    }

                    this.setProductIds(data.favorited_product_ids);
                    setFolderedProductIds(folderedProductIds);

                    if (canRenderFavoriteHearts()) {
                        $('.js-favorite-button.transparent').removeClass('transparent');
                    }

                    this.dataUpdateDeferred.resolve(data);
                });
            } else {
                this.dataUpdateDeferred.resolve();
            }
        }

        return this.dataUpdateDeferred;
    }

    /**
     * @param {number} productId
     * @param {jQuery=} jQueryCountElement
     */
    updateProductFavoriteCount(productId, jQueryCountElement) {
        let $productCount = jQueryCountElement || [];
        if ($productCount.length === 0) {
            $productCount = $(`[data-product-id="${productId}"] ${this.getProductFavoriteCountSelector()}`);
        }

        if ($productCount.length) {
            let currentCount = $productCount.data('favorite-count');
            if (this.isProductIdFavorited(productId)) {
                currentCount += 1;
            } else {
                currentCount -= 1;
            }

            $productCount.data('favorite-count', currentCount);
            $productCount.text(currentCount < 1 ? '' : abbreviateNumber(currentCount));
        }
    }

    /**
     * Draws favorites buttons. Since we use an eventual consistency model this is usually called
     * after a favorite button has been initially drawn to ensure it maintains accuracy.
     *
     * @param {boolean} isFavorite
     * @param {number} productId
     * @param {jQuery=} jQueryHeart
     */
    drawButton(isFavorite, productId, jQueryHeart) {
        if (canRenderFavoriteHearts()) {
            let $buttons = jQueryHeart || [];
            if ($buttons.length === 0) {
                $buttons = $(`[data-product-id=${productId}]`).find(this.getFavoriteButtonSelector());
            }

            $buttons.each((_, el) => {
                const $btn = $(el);
                $btn.toggleClass('on', isFavorite);
                $btn.toggleClass('off', !isFavorite);
            });
        }
    }

    /**
     * Draws favorite buttons for all favorited products.
     */
    drawButtons() {
        if (canRenderFavoriteHearts()) {
            const $buttons = $('[data-product-id]');
            const favoriteButtonSelector = this.getFavoriteButtonSelector();

            $buttons.each((i) => {
                const $button = $buttons.eq(i);
                const id = $button.data('product-id');
                const isFavorite = this.isProductIdFavorited(id);

                if (isFavorite) {
                    const $heart = $button.find(favoriteButtonSelector);
                    this.drawButton(isFavorite, id, $heart);
                }
            });
        }
    }

    /**
     * Syncs the favorited products within the rail to the view and draws their buttons.
     *
     * @param {jQuery} $railContainerEl The rail container element containing products
     */
    syncRailFavorites($railContainerEl) {
        this.updateFavoriteProductIds().done(() => {
            const $products = $railContainerEl.find('.js-product');

            Array.from($products).forEach((el) => {
                const id = $(el).data('product-id');
                if (this.isProductIdFavorited(id)) {
                    this.drawButton(true, id);
                }
            });
        });
    }

    /**
     * @param {number} productId
     * @return {boolean} Indication of whether productId was set or not
     */
    setProductId(productId) {
        if (Number.isFinite(productId)) {
            const parsedProductId = parseInt(productId, 10);

            if (!this.isProductIdFavorited(parsedProductId)) {
                addFavoritedProductId(parsedProductId);
                return true;
            }
        }

        return false;
    }

    /**
     * @param {Array.<number>} productIds
     */
    setProductIds(productIds) {
        setFavoritedProductIds(productIds);
    }

    /**
     * @param {number|string} productId
     * @returns {boolean}
     */
    isProductIdFavorited(productId) {
        return getFavoritedProductIds().includes(productId);
    }

    /**
     * @param {number} productId
     * @param {boolean=} shouldPersist Defaults to true
     */
    addProductId(productId, shouldPersist) {
        const shouldPersistDefaulted = shouldPersist !== undefined ? !!shouldPersist : true;

        if (this.setProductId(productId)) {
            if (shouldPersistDefaulted) {
                this.save(productId);
            }
        }
    }

    /**
     * @param {Array.<number>} productIds
     * @param {boolean=} shouldPersist Defaults to true
     */
    addProductIds(productIds, shouldPersist) {
        productIds.forEach((productId) => {
            this.addProductId(productId, shouldPersist);
        });
    }

    /**
     * @param {number} productId
     * @param {boolean=} shouldPersist Defaults to true
     */
    removeProductId(productId, shouldPersist) {
        const shouldPersistDefaulted = shouldPersist !== undefined ? !!shouldPersist : true;

        if (Number.isFinite(productId)) {
            const parsedProductId = parseInt(productId, 10);

            if (this.isProductIdFavorited(parsedProductId)) {
                removeFavoritedProductId(parsedProductId);

                if (shouldPersistDefaulted) {
                    this.delete(parsedProductId);
                }
            }
        }
    }

    /**
     * Hides or displays the button text depending on the favorited status of the product
     *
     * @param {boolean} isFavorite Whether or not the product is favorited
     */
    toggleButtonText(isFavorite) {
        const favoriteButtonSelector = this.getFavoriteButtonSelector();
        const $favoriteButtonText = $(favoriteButtonSelector).find('.js-favorite-button-text');
        $favoriteButtonText.toggleClass('hidden', isFavorite);
    }

    /**
     * If the product is favorited, it will be unfavorited and vice versa.
     *
     * @param {number} productId
     * @param {boolean=} shouldPersist Defaults to true
     * @param {boolean=} isPurchasable Whether the product is purchasable. Defaults to true
     */
    toggleProductId(productId, shouldPersist, isPurchasable) {
        const shouldPersistDefaulted = shouldPersist !== undefined ? !!shouldPersist : true;
        const isPurchasableDefaulted = isPurchasable !== undefined ? !!isPurchasable : true;

        if (this.isProductIdFavorited(productId)) {
            this.removeProductId(productId, shouldPersistDefaulted);
        } else {
            const $product = $(`[data-product-id="${productId}"]`);
            const productPrice = $product.data('product-price');

            if (isPlainObject(window.chairisher.context.RemarketingObject)) {
                if (isPurchasableDefaulted) {
                    trackRemarketingEvent(
                        'viewBasket',
                        [
                            {
                                id: productId,
                                price: productPrice,
                                quantity: 1,
                            },
                        ],
                        undefined,
                        true,
                    );
                } else {
                    trackRemarketingEvent(
                        'trackTransaction',
                        [
                            {
                                id: productId,
                                price: 0,
                                quantity: 1,
                            },
                        ],
                        `Favorites-${Math.floor(Math.random() * 100000000)}`,
                        true,
                    );
                }
            }
            this.addProductId(productId, shouldPersistDefaulted);
        }
    }

    /**
     * Makes sure the product is a favorite before continuing.
     * Waits for the save so an unauthenticated user can sign in first.
     *
     * @param {number} productId
     * @param {function} onComplete
     */
    ensureProductIdFavorited(productId, onComplete) {
        if (this.isProductIdFavorited(productId)) {
            onComplete();
        } else {
            this.toggleProductId(productId);
            $(document).one('favorite.create', onComplete);
        }
    }

    /**
     * Saves a product in the FavoriteProductSet in the database
     *
     * @param {number} productId
     */
    save(productId) {
        this.persist(false, productId);
    }

    /**
     * Deletes a product from the FavoriteProductSet in the database
     *
     * @param {number} productId
     */
    delete(productId) {
        this.persist(true, productId);
    }

    /**
     * Creates or deletes a product in/from a FavoriteProductSet in the database
     *
     * @param {boolean} isDelete True indicates the product is being deleted from the FavoriteProductSet
     * @param {number} productId
     *
     * @fires FavoriteProductView#favorite.create
     * @fires FavoriteProductView#favorite.creating
     * @fires FavoriteProductView#favorite.delete
     * @fires FavoriteProductView#favorite.deleting
     */
    persist(isDelete, productId) {
        let url;
        if (isDelete) {
            url = getFavoriteDeleteUrl(productId);
        } else {
            url = getFavoriteCreateUrl(productId);
        }

        const isUserAuthenticated = isAuthenticated();
        if (isUserAuthenticated || getFavoritedProductIds().length >= this.anonymousFavoriteThreshold) {
            this.favoriteCount += isDelete ? -1 : 1;
            this.updateFavoriteCount();
            const $product = $(`[data-product-id="${productId}"]`);

            // TODO: (CHAIR-9033) update favorite.js to have an element to trigger this event instead of document
            const eventName = isDelete ? 'deleting' : 'creating';
            $(document).trigger(`favorite.${eventName}`, {
                $el: $product,
                productId,
            });

            const productIds = encodeURIComponent(JSON.stringify([productId]));

            $.ajax({
                // authUrl is used in case the account isn't logged in...
                authUrl: `${getQuickCreateUrl('favorite')}&product-ids=${productIds}`,
                method: 'POST',
                url,
            })
                .done(() => {
                    const action = isDelete ? 'unfavorite' : 'favorite';
                    if (!isUserAuthenticated && !isDelete && !this.isProductIdFavorited(productId)) {
                        addFavoritedProductId(productId);
                    }

                    const productData =
                        $product.data('product-unit-type') === UNIT_TYPE_SAMPLE
                            ? getDataForVariantId(productId)
                            : getProductObjectById(productId);

                    const product = new ProductModel(productData);

                    trackFavoritingEvent(action, this.getFavoriteTrackingLabel(), product);

                    // TODO: (CHAIR-9033) update favorite.js to have an element to trigger this event instead of document
                    const completionEventName = isDelete ? 'delete' : 'create';
                    $(document).trigger(`favorite.${completionEventName}`, {
                        $el: $product,
                        productId,
                    });
                })
                .fail(() => {
                    // if something happens serverside, remove the favorite.
                    // if the user was creating a new favorite it will be turned off.
                    // if the user was unfavoriting a product it will appear unfavorited.
                    this.removeProductId(productId, false);
                    this.drawButton(false, productId);

                    this.favoriteCount += isDelete ? 1 : -1;
                    this.updateFavoriteCount();

                    if (!isUserAuthenticated && !isDelete) {
                        this.updateProductFavoriteCount(productId);
                    }
                });
        }
    }
}

export default new FavoriteProductView();
